/**
 * Music Playlist Redux Slice
 * 
 * Manages the music playlist state including:
 * - Current playlist and tracks
 * - Current track index
 * - Shuffle and loop modes (with actual shuffling logic)
 * - Playlist loading state
 * - R2 bucket data fetching
 * 
 * Follows Single Responsibility Principle: Only handles playlist state, 
 * not player state (that's in SimpleMusicPlayerContext)
 */

import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import type { RootState } from '@store/index';
import { MusicPlaylistState, Playlist, PlaylistDataSource } from '@/types/MusicPlaylist';
import { fetchPlaylistFromR2, refreshPlaylistFromR2 } from './musicPlaylistThunks';
import { cleanFetchPlaylist, cleanRefreshPlaylist } from './cleanPlaylistThunks';

/**
 * Fisher-Yates shuffle algorithm
 * Creates a new shuffled array without modifying the original
 */
const shuffleArray = <T,>(array: T[]): T[] => {
  const shuffled = [...array];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled;
};

const initialState: MusicPlaylistState = {
  playlist: null,
  currentTrackIndex: 0,
  isShuffleEnabled: false,
  loopMode: 'all', // Default to loop all for better UX
  isLoading: false,
  error: null,
  dataSource: null,
  isStale: false,
};

const musicPlaylistSlice = createSlice({
  name: 'musicPlaylist',
  initialState,
  reducers: {
    /**
     * Set the entire playlist
     */
    setPlaylist: (state, action: PayloadAction<Playlist>) => {
      state.playlist = action.payload;
      state.currentTrackIndex = 0;
      state.error = null;
      // Apply shuffle if enabled
      if (state.isShuffleEnabled && state.playlist.tracks.length > 0) {
        state.playlist.tracks = shuffleArray(state.playlist.tracks);
      }
    },

    /**
     * Set playlist with metadata (data source and staleness)
     */
    setPlaylistWithMetadata: (
      state,
      action: PayloadAction<{
        playlist: Playlist;
        dataSource: PlaylistDataSource;
        isStale: boolean;
      }>
    ) => {
      state.playlist = action.payload.playlist;
      state.currentTrackIndex = 0;
      state.error = null;
      state.dataSource = action.payload.dataSource;
      state.isStale = action.payload.isStale;
      // Apply shuffle if enabled
      if (state.isShuffleEnabled && state.playlist.tracks.length > 0) {
        state.playlist.tracks = shuffleArray(state.playlist.tracks);
      }
    },

    /**
     * Set data source explicitly
     */
    setDataSource: (state, action: PayloadAction<PlaylistDataSource>) => {
      state.dataSource = action.payload;
    },

    /**
     * Set isStale flag explicitly
     */
    setIsStale: (state, action: PayloadAction<boolean>) => {
      state.isStale = action.payload;
    },

    /**
     * Toggle shuffle mode and reshuffle playlist if needed
     */
    toggleShuffle: (state) => {
      state.isShuffleEnabled = !state.isShuffleEnabled;

      if (!state.playlist || state.playlist.tracks.length === 0) return;

      if (state.isShuffleEnabled) {
        // Shuffle the playlist
        state.playlist.tracks = shuffleArray(state.playlist.tracks);
        state.currentTrackIndex = 0;
      }
      // When disabling shuffle, we could restore original order, but that's complex
      // So we just keep the current shuffled order
    },

    /**
     * Set shuffle mode explicitly
     */
    setShuffle: (state, action: PayloadAction<boolean>) => {
      const shouldShuffle = action.payload;

      if (shouldShuffle === state.isShuffleEnabled) return; // No change

      state.isShuffleEnabled = shouldShuffle;

      if (!state.playlist || state.playlist.tracks.length === 0) return;

      if (shouldShuffle) {
        // Shuffle the playlist
        state.playlist.tracks = shuffleArray(state.playlist.tracks);
        state.currentTrackIndex = 0;
      }
    },

    /**
     * Move to next track (respects shuffle and loop modes)
     */
    nextTrack: (state) => {
      if (!state.playlist) return;

      const totalTracks = state.playlist.tracks.length;

      if (state.loopMode === 'one') {
        // Loop mode 'one' keeps the same track
        return;
      }

      const nextIndex = state.currentTrackIndex + 1;

      if (nextIndex >= totalTracks) {
        // Reached the end
        if (state.loopMode === 'all') {
          state.currentTrackIndex = 0;
        }
        // If loop mode is 'none', stay at the last track
      } else {
        state.currentTrackIndex = nextIndex;
      }
    },

    /**
     * Move to previous track
     */
    previousTrack: (state) => {
      if (!state.playlist) return;

      const totalTracks = state.playlist.tracks.length;
      const prevIndex = state.currentTrackIndex - 1;

      if (prevIndex < 0) {
        // Reached the beginning
        if (state.loopMode === 'all') {
          state.currentTrackIndex = totalTracks - 1;
        } else {
          state.currentTrackIndex = 0;
        }
      } else {
        state.currentTrackIndex = prevIndex;
      }
    },

    /**
     * Jump to specific track index
     */
    setCurrentTrackIndex: (state, action: PayloadAction<number>) => {
      if (!state.playlist) return;

      const index = action.payload;
      const totalTracks = state.playlist.tracks.length;

      if (index >= 0 && index < totalTracks) {
        state.currentTrackIndex = index;
      }
    },

    /**
     * Cycle through loop modes: none -> all -> one -> none
     */
    cycleLoopMode: (state) => {
      const modes: Array<'none' | 'all' | 'one'> = ['none', 'all', 'one'];
      const currentIndex = modes.indexOf(state.loopMode);
      const nextIndex = (currentIndex + 1) % modes.length;
      state.loopMode = modes[nextIndex];
    },

    /**
     * Set loop mode explicitly
     */
    setLoopMode: (state, action: PayloadAction<'none' | 'all' | 'one'>) => {
      state.loopMode = action.payload;
    },

    /**
     * Set loading state
     */
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
    },

    /**
     * Set error state
     */
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
      state.isLoading = false;
    },

    /**
     * Clear error
     */
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    // Handle fetchPlaylistFromR2
    builder
      .addCase(fetchPlaylistFromR2.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchPlaylistFromR2.fulfilled, (state, action) => {
        state.isLoading = false;
        state.playlist = action.payload.playlist;
        state.dataSource = action.payload.dataSource;
        state.isStale = action.payload.isStale;
        state.currentTrackIndex = 0;
        state.error = null;

        // Apply shuffle if enabled
        if (state.isShuffleEnabled && state.playlist.tracks.length > 0) {
          state.playlist.tracks = shuffleArray(state.playlist.tracks);
        }
      })
      .addCase(fetchPlaylistFromR2.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload ?? 'Failed to fetch playlist';
        state.dataSource = null;
        state.isStale = false;
      });

    // Handle refreshPlaylistFromR2
    builder
      .addCase(refreshPlaylistFromR2.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(refreshPlaylistFromR2.fulfilled, (state, action) => {
        state.isLoading = false;

        // Capture the currently playing track ID *before* replacing the playlist so
        // we can restore the correct position after a re-shuffle (prevents the
        // player from jumping to a different track on every background refresh).
        const currentTrackId = state.playlist?.tracks[state.currentTrackIndex]?.id ?? null;

        state.playlist = action.payload.playlist;
        state.dataSource = action.payload.dataSource;
        state.isStale = action.payload.isStale;
        state.error = null;

        // Apply shuffle if enabled
        if (state.isShuffleEnabled && state.playlist.tracks.length > 0) {
          state.playlist.tracks = shuffleArray(state.playlist.tracks);

          // Restore the current track's position in the new shuffled order.
          // Only fall back to index 0 when there was no previous track.
          if (currentTrackId) {
            const newIndex = state.playlist.tracks.findIndex(t => t.id === currentTrackId);
            state.currentTrackIndex = newIndex !== -1 ? newIndex : 0;
          } else {
            state.currentTrackIndex = 0;
          }
        } else {
          // Without shuffle, preserve the current track by ID (handles server-side
          // reordering when new tracks are added) and fall back to index 0.
          if (currentTrackId) {
            const newIndex = state.playlist.tracks.findIndex(t => t.id === currentTrackId);
            state.currentTrackIndex = newIndex !== -1 ? newIndex : 0;
          } else if (state.currentTrackIndex >= action.payload.playlist.tracks.length) {
            state.currentTrackIndex = 0;
          }
        }
      })
      .addCase(refreshPlaylistFromR2.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload ?? 'Failed to refresh playlist';
      });

    // ========================================================================
    // Clean Architecture Thunks (Strangler Pattern)
    // These handle the same state transitions as legacy thunks above
    // ========================================================================

    // Handle cleanFetchPlaylist
    builder
      .addCase(cleanFetchPlaylist.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(cleanFetchPlaylist.fulfilled, (state, action) => {
        state.isLoading = false;
        state.playlist = action.payload.playlist;
        state.dataSource = action.payload.dataSource;
        state.isStale = action.payload.isStale;
        state.currentTrackIndex = 0;
        state.error = null;

        // Apply shuffle if enabled
        if (state.isShuffleEnabled && state.playlist.tracks.length > 0) {
          state.playlist.tracks = shuffleArray(state.playlist.tracks);
        }
      })
      .addCase(cleanFetchPlaylist.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload ?? 'Failed to fetch playlist';
        state.dataSource = null;
        state.isStale = false;
      });

    // Handle cleanRefreshPlaylist
    builder
      .addCase(cleanRefreshPlaylist.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(cleanRefreshPlaylist.fulfilled, (state, action) => {
        state.isLoading = false;

        // Capture the currently playing track ID *before* replacing the playlist so
        // we can restore the correct position after a re-shuffle.
        const currentTrackId = state.playlist?.tracks[state.currentTrackIndex]?.id ?? null;

        state.playlist = action.payload.playlist;
        state.dataSource = action.payload.dataSource;
        state.isStale = action.payload.isStale;
        state.error = null;

        // Apply shuffle if enabled
        if (state.isShuffleEnabled && state.playlist.tracks.length > 0) {
          state.playlist.tracks = shuffleArray(state.playlist.tracks);

          // Restore the current track's position in the new shuffled order.
          if (currentTrackId) {
            const newIndex = state.playlist.tracks.findIndex(t => t.id === currentTrackId);
            state.currentTrackIndex = newIndex !== -1 ? newIndex : 0;
          } else {
            state.currentTrackIndex = 0;
          }
        } else {
          // Without shuffle, preserve the current track by ID.
          if (currentTrackId) {
            const newIndex = state.playlist.tracks.findIndex(t => t.id === currentTrackId);
            state.currentTrackIndex = newIndex !== -1 ? newIndex : 0;
          } else if (state.currentTrackIndex >= action.payload.playlist.tracks.length) {
            state.currentTrackIndex = 0;
          }
        }
      })
      .addCase(cleanRefreshPlaylist.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload ?? 'Failed to refresh playlist';
      });
  },
});

// Actions
export const {
  setPlaylist,
  setPlaylistWithMetadata,
  setDataSource,
  setIsStale,
  nextTrack,
  previousTrack,
  setCurrentTrackIndex,
  toggleShuffle,
  setShuffle,
  cycleLoopMode,
  setLoopMode,
  setLoading,
  setError,
  clearError,
} = musicPlaylistSlice.actions;

// Thunks (re-export for convenience)
export { fetchPlaylistFromR2, refreshPlaylistFromR2 } from './musicPlaylistThunks';

// Selectors
export const selectPlaylist = (state: RootState) => state.musicPlaylist.playlist;
export const selectCurrentTrackIndex = (state: RootState) => state.musicPlaylist.currentTrackIndex;
export const selectCurrentTrack = (state: RootState) => {
  const { playlist, currentTrackIndex } = state.musicPlaylist;
  return playlist?.tracks[currentTrackIndex] ?? null;
};
export const selectIsShuffleEnabled = (state: RootState) => state.musicPlaylist.isShuffleEnabled;
export const selectLoopMode = (state: RootState) => state.musicPlaylist.loopMode;
export const selectIsLoading = (state: RootState) => state.musicPlaylist.isLoading;
export const selectError = (state: RootState) => state.musicPlaylist.error;
export const selectDataSource = (state: RootState) => state.musicPlaylist.dataSource;
export const selectIsStale = (state: RootState) => state.musicPlaylist.isStale;
export const selectTotalTracks = (state: RootState) => state.musicPlaylist.playlist?.tracks.length ?? 0;
export const selectHasNextTrack = (state: RootState) => {
  const { currentTrackIndex, playlist, loopMode } = state.musicPlaylist;
  if (!playlist) return false;

  const totalTracks = playlist.tracks.length;
  return currentTrackIndex < totalTracks - 1 || loopMode === 'all';
};
export const selectHasPreviousTrack = (state: RootState) => {
  const { currentTrackIndex, loopMode } = state.musicPlaylist;
  return currentTrackIndex > 0 || loopMode === 'all';
};

export default musicPlaylistSlice.reducer;
